import { useMutation } from '@apollo/client';
import {
  PublicApiAllowedOperation,
  PublicApiKeyItem,
  UpdatePublicApiKeyInput,
} from '@provision/entity-types';
import { useCallback } from 'react';
import {
  GET_PUBLIC_API_KEYS_QUERY,
  UPDATE_PUBLIC_API_KEY_MUTATION,
} from 'src/graphql/entities/public-api-key/public-api-key.operations';
import { updateQueryWithInvalidation } from 'src/graphql/entities/public-api-key/public-api-key.utils';
import { useGlobalStore } from 'src/store';
import { useNotifications } from 'src/utils/hooks';

interface UpdatePublicApiKeyMutation {
  updatePublicApiKey: PublicApiKeyItem;
}

interface UpdatePublicApiKeyMutationVariables {
  params: UpdatePublicApiKeyInput;
}

export const useUpdatePublicApiKey = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { showErrorAlert } = useNotifications();

  const [updateMutation, { loading: isUpdating }] = useMutation<
    UpdatePublicApiKeyMutation,
    UpdatePublicApiKeyMutationVariables
  >(UPDATE_PUBLIC_API_KEY_MUTATION, {
    refetchQueries: [GET_PUBLIC_API_KEYS_QUERY],
    awaitRefetchQueries: true,
    update: updateQueryWithInvalidation(selectedPartnerId!),
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to update API key scopes, please try again');
    },
  });

  const updatePublicApiKey = useCallback(
    async (params: { apiKeyId: string; allowedOperations: PublicApiAllowedOperation[] }) => {
      if (!selectedPartnerId) {
        throw new Error('Cannot update API key without a selected partner.');
      }
      const result = await updateMutation({
        variables: {
          params: {
            partnerId: selectedPartnerId,
            apiKeyId: params.apiKeyId,
            allowedOperations: params.allowedOperations,
          },
        },
      });
      return result.data?.updatePublicApiKey;
    },
    [updateMutation, selectedPartnerId],
  );

  return { updatePublicApiKey, isUpdating };
};
